import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, RotateCcw, Trash2, Diff, Clock, ShieldCheck, Loader2, AlertTriangle, RefreshCw, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Textarea } from './ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

export const RestorePointsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [diffView, setDiffView] = useState(null);
  const [restoreView, setRestoreView] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const H = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/super-admin/restore-points/list`, H());
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Load failed');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/api/super-admin/restore-points/create`,
        { name, note }, H());
      toast.success(`Snapshot created: ${r.data.restore_point.id}`);
      setShowCreate(false); setName(''); setNote('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    }
    setCreating(false);
  };

  const compare = async (rp) => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/super-admin/restore-points/${rp.id}/compare`, H());
      setDiffView(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Compare failed');
    }
    setBusy(false);
  };

  const doRestore = async () => {
    if (confirmText !== 'RESTORE') {
      toast.error('Type RESTORE to confirm');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/super-admin/restore-points/${restoreView.id}/restore`,
        { confirm: 'RESTORE', remove_extras: false }, H());
      toast.success(`Restored — ${r.data.counts.applied} questions updated. Backup: ${r.data.pre_restore_backup_id}`);
      setRestoreView(null); setConfirmText('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    }
    setBusy(false);
  };

  const del = async (rp) => {
    if (!window.confirm(`Delete restore point "${rp.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/super-admin/restore-points/${rp.id}`, H());
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const downloadBundle = async (rp) => {
    try {
      const r = await axios.get(`${API}/api/super-admin/restore-points/${rp.id}/sealed-bundle`, {
        ...H(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `MOSAIC-Sealed-Bundle-${rp.id}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sealed bundle downloaded');
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const fmtBytes = b => !b ? '—' : b < 1024*1024 ? `${(b/1024).toFixed(0)} KB` : `${(b/1024/1024).toFixed(1)} MB`;
  const fmtDate = s => !s ? '—' : new Date(s).toLocaleString();

  return (
    <div className="space-y-4" data-testid="restore-points-tab">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck size={20} className="text-emerald-700" />
            Restore Points
          </h2>
          <p className="text-sm text-slate-600">
            Point-in-time snapshots of the MOSAIC content corpus. One-click revert if anything gets corrupted or drifts.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="rp-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="rp-create-btn">
            <Save size={16} className="mr-2" /> Create Restore Point
          </Button>
        </div>
      </div>

      <Card className="p-3 border-l-4 border-l-blue-500 bg-blue-50 text-xs text-blue-900">
        <b>How this works:</b> Each restore point captures the current stems, options, answer keys,
        image pointers, and WIDA metadata for all questions. Restoring always creates an "auto pre-restore
        backup" first, so every restore is itself reversible. Image and audio files are already immutable
        on the CDN — restore just repoints the questions.
      </Card>

      {loading ? (
        <div className="text-center text-slate-400 py-8">Loading…</div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-slate-500" data-testid="rp-empty">
          <ShieldCheck size={48} className="mx-auto text-slate-300 mb-2" />
          <div className="font-semibold text-slate-700">No restore points yet</div>
          <div className="text-sm mt-1">Click <b>Create Restore Point</b> to take your first snapshot.</div>
        </Card>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="rp-table">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">Questions</th>
                <th className="text-left px-3 py-2">Size</th>
                <th className="text-left px-3 py-2 font-mono">SHA</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(rp => (
                <tr key={rp.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`rp-row-${rp.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{rp.name}</div>
                    {rp.note && <div className="text-xs text-slate-500 mt-0.5">{rp.note}</div>}
                    {rp.id.startsWith('rp_pre_') && (
                      <div className="text-[10px] text-amber-600 mt-1 inline-flex items-center gap-1">
                        <AlertTriangle size={10} /> Auto pre-restore backup
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div className="flex items-center gap-1 text-slate-700"><Clock size={11} /> {fmtDate(rp.created_at)}</div>
                    <div className="text-slate-500 mt-0.5">{rp.created_by}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{rp.counts?.questions || 0}</td>
                  <td className="px-3 py-2 text-xs">{fmtBytes(rp.size_bytes_gz)}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{rp.sha256?.slice(0, 12)}…</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="outline" onClick={() => compare(rp)} title="Compare to live" className="h-7 px-2" data-testid={`rp-compare-${rp.id}`}>
                        <Diff size={12} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadBundle(rp)} title="Download as Sealed Bundle (ZIP)" className="h-7 px-2 text-indigo-700" data-testid={`rp-bundle-${rp.id}`}>
                        <Download size={12} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setRestoreView(rp); setConfirmText(''); }} title="Restore to this point" className="h-7 px-2 text-emerald-700" data-testid={`rp-restore-${rp.id}`}>
                        <RotateCcw size={12} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => del(rp)} title="Delete" className="h-7 px-2 text-rose-600" data-testid={`rp-delete-${rp.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !creating && setShowCreate(false)}>
          <Card className="w-full max-w-md p-5" onClick={e => e.stopPropagation()} data-testid="rp-create-modal">
            <h3 className="text-lg font-bold mb-3">Create Restore Point</h3>
            <label className="text-xs uppercase text-slate-500 block mb-1">Name (optional)</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Pre-presentation snapshot" data-testid="rp-create-name" />
            <label className="text-xs uppercase text-slate-500 block mb-1 mt-3">Note (optional)</label>
            <Textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Locked corpus after Feb 11 Vision-AI audit" data-testid="rp-create-note" />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</Button>
              <Button onClick={create} disabled={creating} className="bg-emerald-600 text-white" data-testid="rp-create-submit">
                {creating && <Loader2 className="animate-spin mr-2" size={14} />}
                Create Snapshot
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Diff modal */}
      {diffView && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setDiffView(null)}>
          <Card className="w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()} data-testid="rp-diff-modal">
            <h3 className="text-lg font-bold mb-1">Compare to Live</h3>
            <div className="text-sm text-slate-600 mb-4">Snapshot: <b>{diffView.restore_point.name}</b></div>
            <div className="grid grid-cols-4 gap-2 mb-5">
              <StatCard label="Would change" value={diffView.diff.would_change} color="amber" />
              <StatCard label="Would re-add" value={diffView.diff.would_re_add} color="blue" />
              <StatCard label="Would remove" value={diffView.diff.would_remove} color="rose" />
              <StatCard label="Unchanged" value={diffView.diff.unchanged} color="emerald" />
            </div>
            {diffView.diff.sample_changes?.length > 0 && (
              <>
                <h4 className="text-sm font-bold mb-2">Sample changes (first 20)</h4>
                <div className="border rounded overflow-hidden text-xs">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr><th className="text-left px-2 py-1">Question</th><th className="text-left px-2 py-1">Grade</th><th className="text-left px-2 py-1">Changed fields</th></tr>
                    </thead>
                    <tbody>
                      {diffView.diff.sample_changes.map(c => (
                        <tr key={c.qid} className="border-t">
                          <td className="px-2 py-1 font-mono">{c.qid.slice(0,12)}</td>
                          <td className="px-2 py-1">G{c.grade} {c.modality}</td>
                          <td className="px-2 py-1 text-slate-600">{c.fields.join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={() => setDiffView(null)}>Close</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Restore modal */}
      {restoreView && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !busy && setRestoreView(null)}>
          <Card className="w-full max-w-md p-5" onClick={e => e.stopPropagation()} data-testid="rp-restore-modal">
            <h3 className="text-lg font-bold mb-1 flex items-center gap-2 text-emerald-700">
              <RotateCcw size={18} /> Restore MOSAIC content
            </h3>
            <div className="text-sm text-slate-700 mt-2">
              You're about to restore <b>{restoreView.name}</b> ({restoreView.counts?.questions} questions).
            </div>
            <div className="text-xs text-slate-600 mt-3 space-y-1">
              <div>• The current live state will be auto-backed-up first — this restore is reversible.</div>
              <div>• Student sessions, results, quotes, and orders are NOT touched.</div>
              <div>• Image and audio files on the CDN are NOT touched — only the pointers.</div>
            </div>
            <label className="text-xs uppercase text-slate-500 block mt-4 mb-1">Type <span className="font-mono font-bold">RESTORE</span> to confirm</label>
            <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="RESTORE" data-testid="rp-restore-confirm" />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setRestoreView(null)} disabled={busy}>Cancel</Button>
              <Button onClick={doRestore} disabled={busy || confirmText !== 'RESTORE'} className="bg-emerald-600 text-white" data-testid="rp-restore-submit">
                {busy && <Loader2 className="animate-spin mr-2" size={14} />}
                Restore Now
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className={`bg-${color}-50 border border-${color}-200 rounded p-2 text-center`}>
    <div className={`text-xs text-${color}-700`}>{label}</div>
    <div className={`text-xl font-bold text-${color}-900`}>{value}</div>
  </div>
);

export default RestorePointsTab;
